import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { DeviceStatusCellRenderer } from '@/components/grid/cells/DeviceStatusCellRenderer';
import { ColumnID } from '@/components/grid/column-ids';
import { PoGrid } from '@/components/grid/PoGrid';
import RequirePermissionComponent from '@/components/permissions/RequirePermissionComponent';
import { useApiCall } from '@/hooks/useApiCall';
import { useGridColumnState } from '@/hooks/useGridColumnState';
import { useTranslation } from '@/lib';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { UserSettingKey } from '@/modules/users/api/user-settings/user-setting.contracts';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useLayoutActions } from '@/providers/layout-actions-provider/LayoutActionsProvider';
import { AddOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { GridOptions, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeviceType } from '../../api/device/device.contracts';
import { deviceService } from '../../api/device/device.service';
import { DeviceModel } from '../../types/DeviceModel';

export const AllDevicesPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setActions } = useLayoutActions();

  const { data, isLoading, isError } = useApiCall<DeviceModel[]>(() => deviceService.getAll());

  const { setColumnStateGridApi, handleColumnStateChange, columnState, applyStateToDefinitions, setIsAutoSaveEnabled } = useGridColumnState(
    UserSettingKey.DEVICES_GRID_COLUMN_STATE,
  );

  useEffect(() => {
    setIsAutoSaveEnabled(true);
  }, [setIsAutoSaveEnabled]);

  const onGridReady = (event: GridReadyEvent) => {
    setColumnStateGridApi(event.api);
  };

  const customGridOptions: GridOptions<DeviceModel> = {
    onRowDoubleClicked(event: RowDoubleClickedEvent<DeviceModel, unknown>) {
      navigate(`../${event.data?.id}/details`);
    },
    onGridReady,
    // Handlers for column state changes
    onColumnMoved: handleColumnStateChange,
    onColumnVisible: handleColumnStateChange,
    onColumnResized: handleColumnStateChange,
    onColumnPinned: handleColumnStateChange,
    onSortChanged: handleColumnStateChange,
  };

  const colDefs: ColDefOrGroup<DeviceModel>[] = useMemo(() => {
    const cols: ColDefOrGroup<DeviceModel>[] = [
      {
        colId: ColumnID.DEVICE_NAME,
        headerName: t('name'),
        field: 'name',
        width: 250,
      },
      {
        colId: ColumnID.DEVICE_TYPE,
        headerName: t('type'),
        field: 'deviceType',
        valueFormatter: (params) => {
          switch (params.value) {
            case DeviceType.BLE_GATEWAY:
              return t(`ble_gateway`);
            case DeviceType.MOBILE_SCANNER:
              return t(`mobile_scanner`);
            case DeviceType.FIXED_RFID_READER:
              return t(`fixed_rfid_reader`);
            default:
              return '';
          }
        },
      },
      {
        colId: ColumnID.DEVICE_BRAND,
        headerName: t('brand'),
        field: 'brand',
      },
      {
        colId: ColumnID.DEVICE_UNIQUE_IDENTIFIER,
        headerName: t('tracker_unique_identifier'),
        field: 'identifier',
      },
      {
        colId: ColumnID.DEVICE_TYPE_DESCRIPTION,
        headerName: t('description'),
        field: 'type',
      },
      {
        colId: ColumnID.DEVICE_SERIAL_NUMBER,
        headerName: t('serial_number'),
        field: 'serialNumber',
      },
      {
        colId: ColumnID.DEVICE_LAST_EVENT_DATE,
        headerName: t('last_event_received_at'),
        field: 'lastEventReceivedAt',
        cellRenderer: RelativeDateCellRenderer,
        flex: 1,
      },
      {
        colId: ColumnID.DEVICE_STATUS,
        headerName: t('status'),
        cellRenderer: DeviceStatusCellRenderer,
        sortable: false,
      },
    ];

    // Apply saved column state to the column definitions
    applyStateToDefinitions(cols);

    return cols;
  }, [t, applyStateToDefinitions, columnState]);

  useEffect(() => {
    setActions(
      <RequirePermissionComponent permission={Permission.DEVICES_EDIT}>
        <Button startIcon={<AddOutlined></AddOutlined>} onClick={() => navigate('../create')}>
          {t('create')}
        </Button>
      </RequirePermissionComponent>,
    );

    return () => {
      setActions('');
    };
  }, []);

  return (
    <TabbedPageLayout>
      <TabbedPageLayoutBody>
        <div className="flex h-full flex-grow flex-col">
          {isError ? (
            <ErrorLoadingDataAlert />
          ) : (
            <PoGrid
              isLoading={isLoading}
              colDefs={colDefs}
              rowData={data}
              gridOptions={customGridOptions}
              disableResizeColumnsToFit
              disableDefaultGridOptions
            />
          )}
        </div>
      </TabbedPageLayoutBody>
    </TabbedPageLayout>
  );
};
